/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";



const defaultCategory = {
  business_unit_id: "",
  name: "",
  description: "",
  max_adults: 2,
  max_children: 0,
  max_occupancy: 2,
};


function RoomCategoriesList() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  // 🔵 Popup
  const [showNewPopup, setShowNewPopup] = useState(false);
  const [newCategory, setNewCategory] = useState(defaultCategory);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  
  // 🆕 Edit popup
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editCategory, setEditCategory] = useState(null);

  function openEditPopup(cat) {
    const fallbackMaxOccupancy =
      (Number(cat.max_adults || 0) || 0) + (Number(cat.max_children || 0) || 0);

    setEditCategory({
      ...cat,
      max_occupancy: cat.max_occupancy ?? cat.maxOccupancy ?? fallbackMaxOccupancy,
    });
    setShowEditPopup(true);
  }

  // Selector de business units
  const [businessUnits, setBusinessUnits] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [columnFilters, setColumnFilters] = useState({
    id: [],
    businessUnits: [],
    names: [],
    description: "",
    maxAdults: [],
    maxChildren: [],
    maxOccupancy: [],
  });
  const [activeFilter, setActiveFilter] = useState(null); // { key, rect }
  const [filterOptionQuery, setFilterOptionQuery] = useState("");
  const filterPopoverRef = useRef(null);

  const businessUnitById = useMemo(() => {
    const map = new Map();
    businessUnits.forEach((bu) => {
      map.set(String(bu.id), bu);
    });
    return map;
  }, [businessUnits]);

  const filteredCategories = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    const idSet = new Set((columnFilters.id || []).map((x) => Number(x)));
    const buSet = new Set((columnFilters.businessUnits || []).map((x) => String(x)));
    const namesSet = new Set((columnFilters.names || []).map((x) => String(x).toLowerCase()));
    const descriptionNeedle = String(columnFilters.description || "").trim().toLowerCase();
    const maxAdultsSet = new Set((columnFilters.maxAdults || []).map((x) => Number(x)));
    const maxChildrenSet = new Set((columnFilters.maxChildren || []).map((x) => Number(x)));
    const maxOccupancySet = new Set((columnFilters.maxOccupancy || []).map((x) => Number(x)));

    return categories.filter((cat) => {
      const catIdNum = Number(cat.id);
      const buIdStr = String(cat.business_unit_id);
      const nameStr = String(cat.name || "");
      const descStr = String(cat.description || "");
      const maxAdultsNum = Number(cat.max_adults);
      const maxChildrenNum = Number(cat.max_children);
      const maxOccupancyNum = Number(
        cat.max_occupancy ??
          cat.maxOccupancy ??
          (Number(cat.max_adults || 0) + Number(cat.max_children || 0))
      );

      if (idSet.size && !idSet.has(catIdNum)) return false;
      if (buSet.size && !buSet.has(buIdStr)) return false;
      if (namesSet.size && !namesSet.has(nameStr.toLowerCase())) return false;
      if (descriptionNeedle && !descStr.toLowerCase().includes(descriptionNeedle)) return false;
      if (maxAdultsSet.size && !maxAdultsSet.has(maxAdultsNum)) return false;
      if (maxChildrenSet.size && !maxChildrenSet.has(maxChildrenNum)) return false;
      if (maxOccupancySet.size && !maxOccupancySet.has(maxOccupancyNum)) return false;

      if (!q) return true;

      const bu = businessUnitById.get(String(cat.business_unit_id));
      const haystack = [
        cat.id,
        cat.business_unit_id,
        bu?.name,
        cat.name,
        cat.description,
        cat.max_adults,
        cat.max_children,
        cat.max_occupancy,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [businessUnitById, categories, columnFilters, searchQuery]);

  const columnIsFiltered = (key) => {
    const value = columnFilters[key];
    return Array.isArray(value) ? value.length > 0 : String(value || "").trim().length > 0;
  };

  const columnSubtitle = (key) => {
    const value = columnFilters[key];

    if (Array.isArray(value)) {
      if (!value.length) return "All";

      if (value.length === 1) {
        const only = value[0];
        if (key === "id") return `#${only}`;
        if (key === "businessUnits") {
          return businessUnitById.get(String(only))?.name ?? `ID ${only}`;
        }
        if (key === "names") {
          const k = String(only || "").toLowerCase();
          const displayName = nameStats.options.find((o) => o.key === k)?.name;
          return displayName ?? String(only);
        }
        return String(only);
      }

      if (key === "id") return `${value.length} IDs`;
      if (key === "businessUnits") return `${value.length} units`;
      if (key === "names") return `${value.length} names`;
      return `${value.length} selected`;
    }

    const needle = String(value || "").trim();
    if (!needle) return "All";
    if (key === "description") return `Contains: ${needle}`;
    return needle;
  };

  const closeFilterPopover = () => setActiveFilter(null);

  const toggleFilterPopover = (key, el) => {
    setActiveFilter((prev) => {
      if (prev?.key === key) return null;
      return { key, rect: el.getBoundingClientRect() };
    });
  };

  useEffect(() => {
    setFilterOptionQuery("");
  }, [activeFilter?.key]);

  useEffect(() => {
    if (!activeFilter?.key) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeFilterPopover();
    };

    const onMouseDown = (e) => {
      if (filterPopoverRef.current?.contains(e.target)) return;
      if (e.target?.closest?.("[data-rc-filter-trigger]")) return;
      closeFilterPopover();
    };

    const onResize = () => closeFilterPopover();

    const onScroll = (e) => {
      if (filterPopoverRef.current?.contains(e.target)) return;
      closeFilterPopover();
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("mousedown", onMouseDown);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [activeFilter?.key]);

  useEffect(() => {
    async function fetchBusinessUnits() {
      try {
        const token = localStorage.getItem("palantir_token");
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/business-units/all`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBusinessUnits(res.data);
      } catch (err) {
        setBusinessUnits([]);
        console.error("❌ Error cargando business units para selector:", err);
      }
    }
    fetchBusinessUnits();
  }, []);

  useEffect(() => {
    async function fetchCategories() {
      try {
        const token = localStorage.getItem("palantir_token");
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/room_categories`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCategories(res.data);
      } catch (err) {
        console.error("❌ Error cargando Room Categories:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchCategories();
  }, []);

  async function handleSave() {
    setSaving(true);
    setError("");
    try {
      const token = localStorage.getItem("palantir_token");
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/room_categories`,
        newCategory,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowNewPopup(false);
      setNewCategory(defaultCategory);
      // Recargar tabla
      const updated = await axios.get(`${process.env.REACT_APP_API_URL}/api/room_categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(updated.data);
    } catch (err) {
      setError(err.response?.data?.error || "Error saving room category.");
    } finally {
      setSaving(false);
    }
  }

  const toggleMultiSelectFilter = (key, value) => {
    setColumnFilters((prev) => {
      const prevArr = Array.isArray(prev[key]) ? prev[key] : [];
      const exists = prevArr.includes(value);
      const nextArr = exists ? prevArr.filter((v) => v !== value) : [...prevArr, value];
      return { ...prev, [key]: nextArr };
    });
  };

  const clearColumnFilter = (key) => {
    setColumnFilters((prev) => ({
      ...prev,
      [key]: Array.isArray(prev[key]) ? [] : "",
    }));
  };

  const idOptions = useMemo(() => {
    const set = new Set();
    categories.forEach((cat) => {
      const n = Number(cat.id);
      if (Number.isFinite(n)) set.add(n);
    });
    return Array.from(set).sort((a, b) => a - b);
  }, [categories]);

  const businessUnitStats = useMemo(() => {
    const counts = new Map();
    categories.forEach((cat) => {
      const id = String(cat.business_unit_id);
      counts.set(id, (counts.get(id) || 0) + 1);
    });

    const map = new Map();
    businessUnits.forEach((bu) => {
      const id = String(bu.id);
      map.set(id, { id, name: String(bu.name || "").trim() || `Business Unit ${id}` });
    });
    categories.forEach((cat) => {
      const id = String(cat.business_unit_id);
      if (!map.has(id)) map.set(id, { id, name: `Business Unit ${id}` });
    });

    const options = Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
    );

    return { options, counts };
  }, [businessUnits, categories]);

  const nameStats = useMemo(() => {
    const counts = new Map(); // lowerName -> count
    const display = new Map(); // lowerName -> displayName

    categories.forEach((cat) => {
      const name = String(cat.name || "").trim();
      if (!name) return;
      const key = name.toLowerCase();
      counts.set(key, (counts.get(key) || 0) + 1);
      if (!display.has(key)) display.set(key, name);
    });

    const options = Array.from(display.entries())
      .map(([key, name]) => ({ key, name }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

    return { options, counts };
  }, [categories]);

  const maxAdultsStats = useMemo(() => {
    const counts = new Map();
    categories.forEach((cat) => {
      const n = Number(cat.max_adults);
      if (!Number.isFinite(n)) return;
      counts.set(n, (counts.get(n) || 0) + 1);
    });
    const options = Array.from(counts.keys()).sort((a, b) => a - b);
    return { options, counts };
  }, [categories]);

  const maxChildrenStats = useMemo(() => {
    const counts = new Map();
    categories.forEach((cat) => {
      const n = Number(cat.max_children);
      if (!Number.isFinite(n)) return;
      counts.set(n, (counts.get(n) || 0) + 1);
    });
    const options = Array.from(counts.keys()).sort((a, b) => a - b);
    return { options, counts };
  }, [categories]);

  const maxOccupancyStats = useMemo(() => {
    const counts = new Map();
    categories.forEach((cat) => {
      const n = Number(
        cat.max_occupancy ??
          cat.maxOccupancy ??
          (Number(cat.max_adults || 0) + Number(cat.max_children || 0))
      );
      if (!Number.isFinite(n)) return;
      counts.set(n, (counts.get(n) || 0) + 1);
    });
    const options = Array.from(counts.keys()).sort((a, b) => a - b);
    return { options, counts };
  }, [categories]);

  const uiCss = `
    .rc-page {
      width: 100%;
      max-width: 100%;
      margin: 0 auto;
      padding: 0px 18px 14px;
      height: 100%;
      min-height: 0;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
    }

    @media (max-width: 768px) {
      .rc-page {
        padding: 0px 12px 12px;
        height: 100%;
      }
    }

    .rc-card {
      position: relative;
      border-radius: 18px;
      border: 1px solid transparent;
      background:
        linear-gradient(180deg, rgba(25, 25, 51, 0.94), rgba(10, 10, 22, 0.94)) padding-box,
        linear-gradient(135deg, rgba(48, 216, 255, 0.55), rgba(168, 85, 247, 0.65), rgba(255, 77, 79, 0.45)) border-box;
      box-shadow: 0 28px 90px rgba(0, 0, 0, 0.58);
      overflow: hidden;
      flex: 1 1 auto;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }

    .rc-card::before {
      content: \"\";
      position: absolute;
      inset: 0;
      background:
        radial-gradient(900px 300px at 10% 0%, rgba(48, 216, 255, 0.14), transparent 60%),
        radial-gradient(700px 260px at 90% 10%, rgba(168, 85, 247, 0.16), transparent 62%),
        radial-gradient(800px 260px at 40% 110%, rgba(255, 77, 79, 0.10), transparent 60%);
      pointer-events: none;
      opacity: 0.95;
    }

    .rc-card > * {
      position: relative;
      z-index: 1;
    }

    .rc-cardHeader {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      gap: 16px;
      padding: 18px 18px 14px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      flex: 0 0 auto;
    }

    @media (max-width: 992px) {
      .rc-cardHeader {
        flex-direction: column;
        align-items: stretch;
      }
    }

    .rc-title {
      margin: 0;
      font-size: 1.35rem;
      font-weight: 800;
      letter-spacing: 0.2px;
      color: transparent;
      background: linear-gradient(
        90deg,
        #e6e6f7 0%,
        rgba(48, 216, 255, 0.95) 40%,
        rgba(168, 85, 247, 0.95) 85%
      );
      -webkit-background-clip: text;
      background-clip: text;
      text-shadow: 0 0 24px rgba(48, 216, 255, 0.12);
    }

    .rc-subtitle {
      margin-top: 4px;
      font-size: 0.85rem;
      color: rgba(230, 230, 247, 0.72);
    }

    .rc-actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 10px;
      flex-wrap: wrap;
    }

    .rc-searchInput {
      width: min(420px, 60vw);
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid rgba(255, 255, 255, 0.10);
      color: rgba(230, 230, 247, 0.92);
      border-radius: 12px;
      padding: 10px 12px;
      outline: none;
      transition: border-color 120ms ease, box-shadow 120ms ease, background 120ms ease;
    }

    .rc-searchInput::placeholder {
      color: rgba(230, 230, 247, 0.45);
    }

    .rc-searchInput:focus {
      background: rgba(255, 255, 255, 0.06);
      border-color: rgba(48, 216, 255, 0.55);
      box-shadow: 0 0 0 4px rgba(48, 216, 255, 0.12);
    }

    .rc-btn {
      border-radius: 12px;
      padding: 10px 14px;
      font-size: 0.93rem;
      font-weight: 700;
      border: 1px solid transparent;
      cursor: pointer;
      transition: transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease, background 120ms ease;
      user-select: none;
    }

    .rc-btn:disabled {
      opacity: 0.55;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }

    .rc-btn--primary {
      color: rgba(230, 230, 247, 0.96);
      border-color: rgba(48, 216, 255, 0.55);
      background: linear-gradient(135deg, rgba(48, 216, 255, 0.18), rgba(168, 85, 247, 0.30));
    }

    .rc-btn--primary:hover {
      transform: translateY(-1px);
      box-shadow: 0 18px 30px rgba(48, 216, 255, 0.14);
    }

    .rc-btn--danger {
      color: rgba(255, 255, 255, 0.92);
      border-color: rgba(255, 77, 79, 0.65);
      background: linear-gradient(135deg, rgba(255, 77, 79, 0.18), rgba(168, 85, 247, 0.22));
    }

    .rc-btn--danger:hover {
      transform: translateY(-1px);
      box-shadow: 0 18px 30px rgba(255, 77, 79, 0.10);
    }

    .rc-btn--ghost {
      color: rgba(230, 230, 247, 0.86);
      border-color: rgba(255, 255, 255, 0.14);
      background: rgba(255, 255, 255, 0.04);
    }

    .rc-btn--ghost:hover {
      transform: translateY(-1px);
      box-shadow: 0 18px 30px rgba(0, 0, 0, 0.30);
    }

    .rc-tableWrap {
      flex: 1 1 auto;
      min-height: 0;
      overflow: auto;
      scrollbar-gutter: stable;
    }

    .rc-table {
      width: 100%;
      min-width: 0;
      table-layout: fixed;
      border-collapse: separate;
      border-spacing: 0;
    }

    .rc-table thead th {
      position: sticky;
      top: 0;
      z-index: 2;
      padding: 0;
      background: linear-gradient(
        180deg,
        rgba(22, 22, 50, 0.92),
        rgba(12, 12, 28, 0.92)
      );
      backdrop-filter: blur(10px);
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.06),
        inset 0 -1px 0 rgba(0, 0, 0, 0.18);
      overflow: hidden;
    }

    .rc-table thead th:first-child {
      border-top-left-radius: 14px;
    }

    .rc-table thead th:last-child {
      border-top-right-radius: 14px;
    }

    .rc-table thead th:not(:last-child) {
      box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.06),
        inset 0 -1px 0 rgba(0, 0, 0, 0.18),
        inset -1px 0 0 rgba(255, 255, 255, 0.06);
    }

    .rc-thBtn {
      position: relative;
      width: 100%;
      border: none;
      background: transparent;
      padding: 12px 38px 12px 14px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      gap: 3px;
      text-align: left;
      cursor: pointer;
      user-select: none;
      color: rgba(230, 230, 247, 0.82);
      transition: background 120ms ease, color 120ms ease, transform 120ms ease;
      min-height: 56px;
    }

    .rc-thBtn:hover {
      background: rgba(255, 255, 255, 0.03);
    }

    .rc-thBtn:active {
      transform: translateY(0.5px);
    }

    .rc-thBtn::after {
      content: "";
      position: absolute;
      left: 14px;
      right: 14px;
      bottom: 0;
      height: 2px;
      border-radius: 999px;
      background: linear-gradient(
        90deg,
        rgba(48, 216, 255, 0.0),
        rgba(48, 216, 255, 0.55),
        rgba(168, 85, 247, 0.55),
        rgba(255, 77, 79, 0.45)
      );
      opacity: 0;
      transition: opacity 120ms ease;
    }

    .rc-thBtn:hover::after {
      opacity: 0.65;
    }

    .rc-thBtn:focus-visible {
      outline: 2px solid rgba(48, 216, 255, 0.70);
      outline-offset: -2px;
    }

    .rc-thBtn--right {
      align-items: flex-end;
      text-align: right;
    }

    .rc-thLabel {
      display: block;
      width: 100%;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-weight: 950;
      font-size: 0.72rem;
      letter-spacing: 0.09em;
      text-transform: uppercase;
      color: rgba(230, 230, 247, 0.78);
    }

    .rc-thSub {
      display: block;
      width: 100%;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-weight: 750;
      font-size: 0.68rem;
      letter-spacing: 0.01em;
      color: rgba(230, 230, 247, 0.48);
    }

    .rc-thDot {
      position: absolute;
      right: 14px;
      top: 50%;
      transform: translateY(-50%);
      width: 8px;
      height: 8px;
      border-radius: 999px;
      background: rgba(48, 216, 255, 0.0);
      box-shadow: 0 0 0 0 rgba(48, 216, 255, 0.0);
      transition: background 120ms ease, box-shadow 120ms ease;
    }

    .rc-thDot--on {
      background: rgba(48, 216, 255, 0.95);
      box-shadow: 0 0 0 4px rgba(48, 216, 255, 0.14);
    }

    .rc-thBtn--filtered {
      color: rgba(178, 245, 255, 0.92);
    }

    .rc-thBtn--filtered .rc-thLabel {
      color: rgba(178, 245, 255, 0.95);
    }

    .rc-thBtn--filtered .rc-thSub {
      color: rgba(178, 245, 255, 0.60);
    }

    .rc-thBtn--filtered::after {
      opacity: 0.95;
    }

    .rc-thBtn--open {
      background: rgba(48, 216, 255, 0.045);
    }

    .rc-thBtn--open::after {
      opacity: 0.75;
    }

    .rc-table tbody td {
      padding: 14px 14px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.07);
      color: rgba(230, 230, 247, 0.88);
      vertical-align: middle;
    }

    .rc-table tbody tr {
      background: rgba(255, 255, 255, 0.02);
      transition: background 120ms ease, transform 120ms ease, box-shadow 120ms ease;
    }

    .rc-table tbody tr:nth-child(even) {
      background: rgba(255, 255, 255, 0.015);
    }

    .rc-table tbody tr:hover {
      background: rgba(48, 216, 255, 0.055);
      transform: translateY(-1px);
      box-shadow: 0 18px 50px rgba(0, 0, 0, 0.25);
    }

    .rc-idPill {
      display: inline-flex;
      align-items: center;
      padding: 4px 10px;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid rgba(255, 255, 255, 0.10);
      font-variant-numeric: tabular-nums;
      color: rgba(230, 230, 247, 0.86);
    }

    .rc-buCell {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
    }

    .rc-buName {
      font-weight: 700;
      color: rgba(230, 230, 247, 0.92);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .rc-buMeta {
      font-size: 0.78rem;
      color: rgba(230, 230, 247, 0.52);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .rc-nameBtn {
      all: unset;
      display: block;
      cursor: pointer;
      font-weight: 800;
      color: rgba(48, 216, 255, 0.95);
      transition: color 120ms ease, text-shadow 120ms ease;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .rc-nameBtn:hover {
      color: rgba(178, 245, 255, 1);
      text-shadow: 0 0 24px rgba(48, 216, 255, 0.35);
    }

    .rc-nameBtn:focus-visible {
      outline: 2px solid rgba(48, 216, 255, 0.7);
      outline-offset: 3px;
      border-radius: 6px;
    }

    .rc-desc {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      color: rgba(230, 230, 247, 0.76);
      max-width: 520px;
    }

    .rc-muted {
      color: rgba(230, 230, 247, 0.40);
    }

    .rc-pill {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 4px 10px;
      border-radius: 999px;
      border: 1px solid rgba(255, 255, 255, 0.10);
      background: rgba(255, 255, 255, 0.03);
      font-variant-numeric: tabular-nums;
      min-width: 44px;
    }

    .rc-pill--accent {
      border-color: rgba(168, 85, 247, 0.40);
      background: rgba(168, 85, 247, 0.16);
    }

    .rc-numCell {
      text-align: right;
    }

    .rc-filterPopover {
      position: fixed;
      z-index: 3000;
      width: min(380px, calc(100vw - 24px));
      border-radius: 16px;
      border: 1px solid transparent;
      background:
        linear-gradient(180deg, rgba(25, 25, 51, 0.96), rgba(10, 10, 22, 0.96)) padding-box,
        linear-gradient(135deg, rgba(48, 216, 255, 0.45), rgba(168, 85, 247, 0.50), rgba(255, 77, 79, 0.28)) border-box;
      box-shadow: 0 28px 90px rgba(0, 0, 0, 0.60);
      overflow: hidden;
    }

    .rc-filterHeader {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      padding: 12px 12px 10px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      background: rgba(255, 255, 255, 0.02);
    }

    .rc-filterTitle {
      font-weight: 900;
      color: rgba(230, 230, 247, 0.92);
      font-size: 0.95rem;
      letter-spacing: 0.2px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .rc-filterHeaderActions {
      display: flex;
      align-items: center;
      gap: 8px;
      flex: 0 0 auto;
    }

    .rc-filterSmallBtn {
      border: 1px solid rgba(255, 255, 255, 0.14);
      background: rgba(255, 255, 255, 0.04);
      color: rgba(230, 230, 247, 0.86);
      padding: 7px 10px;
      border-radius: 12px;
      font-weight: 800;
      font-size: 0.82rem;
      cursor: pointer;
      transition: transform 120ms ease, background 120ms ease, border-color 120ms ease;
    }

    .rc-filterSmallBtn:hover {
      transform: translateY(-1px);
      background: rgba(255, 255, 255, 0.06);
      border-color: rgba(48, 216, 255, 0.28);
    }

    .rc-filterCloseBtn {
      border: 1px solid rgba(255, 255, 255, 0.14);
      background: rgba(255, 255, 255, 0.04);
      color: rgba(230, 230, 247, 0.86);
      width: 34px;
      height: 34px;
      border-radius: 12px;
      cursor: pointer;
      font-size: 1.05rem;
      line-height: 1;
      display: grid;
      place-items: center;
      transition: transform 120ms ease, background 120ms ease, border-color 120ms ease;
    }

    .rc-filterCloseBtn:hover {
      transform: translateY(-1px);
      background: rgba(255, 255, 255, 0.06);
      border-color: rgba(255, 77, 79, 0.35);
    }

    .rc-filterBody {
      padding: 12px;
    }

    .rc-filterInput {
      width: 100%;
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid rgba(255, 255, 255, 0.10);
      color: rgba(230, 230, 247, 0.92);
      border-radius: 12px;
      padding: 10px 12px;
      outline: none;
      transition: border-color 120ms ease, box-shadow 120ms ease, background 120ms ease;
    }

    .rc-filterInput::placeholder {
      color: rgba(230, 230, 247, 0.45);
    }

    .rc-filterInput:focus {
      background: rgba(255, 255, 255, 0.06);
      border-color: rgba(48, 216, 255, 0.55);
      box-shadow: 0 0 0 4px rgba(48, 216, 255, 0.12);
    }

    .rc-filterHint {
      margin-top: 8px;
      font-size: 0.82rem;
      color: rgba(230, 230, 247, 0.62);
    }

    .rc-filterList {
      margin-top: 10px;
      display: flex;
      flex-direction: column;
      gap: 8px;
      max-height: min(320px, 55vh);
      overflow: auto;
      overscroll-behavior: contain;
      padding-right: 2px;
    }

    .rc-filterOption {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 10px;
      border-radius: 14px;
      border: 1px solid rgba(255, 255, 255, 0.08);
      background: rgba(255, 255, 255, 0.02);
      cursor: pointer;
      transition: background 120ms ease, border-color 120ms ease, transform 120ms ease;
      user-select: none;
    }

    .rc-filterOption:hover {
      background: rgba(48, 216, 255, 0.05);
      border-color: rgba(48, 216, 255, 0.16);
      transform: translateY(-1px);
    }

    .rc-filterOption input {
      accent-color: rgba(48, 216, 255, 0.95);
      width: 16px;
      height: 16px;
      flex: 0 0 auto;
    }

    .rc-filterOptionText {
      min-width: 0;
      flex: 1 1 auto;
    }

    .rc-filterOptionLabel {
      font-weight: 850;
      color: rgba(230, 230, 247, 0.90);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .rc-filterOptionMeta {
      margin-top: 2px;
      font-size: 0.78rem;
      color: rgba(230, 230, 247, 0.55);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .rc-filterCountPill {
      flex: 0 0 auto;
      padding: 4px 10px;
      border-radius: 999px;
      border: 1px solid rgba(255, 255, 255, 0.10);
      background: rgba(255, 255, 255, 0.03);
      color: rgba(230, 230, 247, 0.78);
      font-variant-numeric: tabular-nums;
    }

    .rc-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 44px 18px 54px;
      gap: 10px;
      color: rgba(230, 230, 247, 0.72);
      min-height: 100%;
    }

    .rc-stateTitle {
      font-weight: 900;
      font-size: 1.05rem;
      color: rgba(230, 230, 247, 0.92);
    }

    .rc-stateSubtitle {
      font-size: 0.92rem;
      color: rgba(230, 230, 247, 0.65);
      max-width: 520px;
      text-align: center;
    }

    .rc-stateActions {
      margin-top: 6px;
    }

    .rc-spinner {
      width: 42px;
      height: 42px;
      border-radius: 999px;
      border: 3px solid rgba(255, 255, 255, 0.14);
      border-top-color: rgba(48, 216, 255, 0.8);
      animation: rcSpin 900ms linear infinite;
    }

    @keyframes rcSpin {
      to {
        transform: rotate(360deg);
      }
    }

    .rc-tableWrap::-webkit-scrollbar {
      height: 12px;
      width: 12px;
    }

    .rc-tableWrap::-webkit-scrollbar-thumb {
      background: rgba(230, 230, 247, 0.20);
      border-radius: 999px;
      border: 3px solid rgba(0, 0, 0, 0);
      background-clip: padding-box;
    }

    .rc-tableWrap::-webkit-scrollbar-thumb:hover {
      background: rgba(230, 230, 247, 0.30);
      border: 3px solid rgba(0, 0, 0, 0);
      background-clip: padding-box;
    }

    .rc-tableWrap::-webkit-scrollbar-corner {
      background: transparent;
    }
  `;

  const filterModel = useMemo(() => {
    const key = activeFilter?.key;
    if (!key) return null;

    if (key === "description") {
      return { key, title: "Description", type: "text" };
    }

    if (key === "id") {
      return {
        key,
        title: "ID",
        type: "set",
        options: idOptions.map((id) => ({ value: id, label: `#${id}`, count: 1 })),
      };
    }

    if (key === "businessUnits") {
      return {
        key,
        title: "Business unit",
        type: "set",
        options: businessUnitStats.options.map((bu) => ({
          value: bu.id,
          label: bu.name,
          meta: `ID ${bu.id}`,
          count: businessUnitStats.counts.get(bu.id) || 0,
        })),
      };
    }

    if (key === "names") {
      return {
        key,
        title: "Name",
        type: "set",
        options: nameStats.options.map((o) => ({
          value: o.key,
          label: o.name,
          count: nameStats.counts.get(o.key) || 0,
        })),
      };
    }

    if (key === "maxAdults") {
      return {
        key,
        title: "Max adults",
        type: "set",
        options: maxAdultsStats.options.map((n) => ({
          value: n,
          label: String(n),
          count: maxAdultsStats.counts.get(n) || 0,
        })),
      };
    }

    if (key === "maxChildren") {
      return {
        key,
        title: "Max children",
        type: "set",
        options: maxChildrenStats.options.map((n) => ({
          value: n,
          label: String(n),
          count: maxChildrenStats.counts.get(n) || 0,
        })),
      };
    }

    if (key === "maxOccupancy") {
      return {
        key,
        title: "Max occupancy",
        type: "set",
        options: maxOccupancyStats.options.map((n) => ({
          value: n,
          label: String(n),
          count: maxOccupancyStats.counts.get(n) || 0,
        })),
      };
    }

    return null;
  }, [
    activeFilter?.key,
    businessUnitStats,
    idOptions,
    maxAdultsStats,
    maxChildrenStats,
    maxOccupancyStats,
    nameStats,
  ]);

  const filterPopoverPos = useMemo(() => {
    if (!activeFilter?.rect) return null;

    const rect = activeFilter.rect;
    const pad = 12;
    const viewportW = window.innerWidth || 1200;
    const viewportH = window.innerHeight || 800;

    const width = Math.min(380, viewportW - pad * 2);
    const estimatedHeight = 420;

    let left = rect.left;
    left = Math.max(pad, Math.min(left, viewportW - width - pad));

    const showBelow = rect.bottom + 10 + estimatedHeight <= viewportH - pad;
    let top = showBelow ? rect.bottom + 10 : rect.top - estimatedHeight - 10;
    top = Math.max(pad, Math.min(top, viewportH - estimatedHeight - pad));

    return { top, left, width };
  }, [activeFilter?.rect]);

  const visibleFilterOptions = useMemo(() => {
    if (!filterModel || filterModel.type !== "set") return [];
    const needle = filterOptionQuery.trim().toLowerCase();
    if (!needle) return filterModel.options;

    return filterModel.options.filter((o) => {
      const label = String(o.label || "").toLowerCase();
      const meta = String(o.meta || "").toLowerCase();
      return label.includes(needle) || meta.includes(needle);
    });
  }, [filterModel, filterOptionQuery]);

  const selectAllVisibleOptions = () => {
    if (!filterModel || filterModel.type !== "set") return;
    setColumnFilters((prev) => ({
      ...prev,
      [filterModel.key]: visibleFilterOptions.map((o) => o.value),
    }));
  };

  return (
    <div className="rc-page">
      <style>{uiCss}</style>

      <div className="rc-card">
      <div className="rc-cardHeader">
        <div>
          <h2 className="rc-title">Room Categories</h2>
          <div className="rc-subtitle">
            {loading
              ? "Cargando room categories…"
              : categories.length === 0
                ? "Aún no hay room categories."
                : `${filteredCategories.length} de ${categories.length} categorías`}
          </div>
        </div>

        <div className="rc-actions">
          <input
            className="rc-searchInput"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar por ID, business unit, nombre o descripción…"
            disabled={loading}
          />

          <button
            type="button"
            className="rc-btn rc-btn--primary"
            onClick={() => {
              setShowNewPopup(true);
              setError("");
              setNewCategory(defaultCategory);
            }}
          >
            + Add Room Category
          </button>

          <button
            type="button"
            className="rc-btn rc-btn--danger"
            onClick={() => {
              setShowDeletePopup(true);
              setDeleteError("");
              setDeleteId("");
            }}
          >
            Delete Room Category
          </button>
        </div>
      </div>

      <div className="rc-tableWrap">
        {loading ? (
          <div className="rc-state">
            <div className="rc-spinner" aria-hidden="true" />
            <div className="rc-stateSubtitle">Cargando room categories…</div>
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="rc-state">
            <div className="rc-stateTitle">
              {categories.length === 0 ? "No hay room categories." : "Sin resultados."}
            </div>
            <div className="rc-stateSubtitle">
              {categories.length === 0
                ? "Crea tu primera categoría para empezar."
                : "Prueba otra búsqueda o limpia el filtro."}
            </div>
            {searchQuery.trim() ? (
              <div className="rc-stateActions">
                <button
                  type="button"
                  className="rc-btn rc-btn--ghost"
                  onClick={() => setSearchQuery("")}
                >
                  Limpiar búsqueda
                </button>
              </div>
            ) : null}
          </div>
        ) : (
          <table className="rc-table">
            <thead>
              <tr>
                <th style={{ width: "7%" }}>
                  <button
                    type="button"
                    data-rc-filter-trigger="id"
                    className={`rc-thBtn ${columnIsFiltered("id") ? "rc-thBtn--filtered" : ""} ${
                      activeFilter?.key === "id" ? "rc-thBtn--open" : ""
                    }`}
                    onClick={(e) => toggleFilterPopover("id", e.currentTarget)}
                  >
                    <span className="rc-thLabel">ID</span>
                    <span className="rc-thSub">{columnSubtitle("id")}</span>
                    <span
                      className={`rc-thDot ${columnIsFiltered("id") ? "rc-thDot--on" : ""}`}
                      aria-hidden="true"
                    />
                  </button>
                </th>
                <th style={{ width: "21%" }}>
                  <button
                    type="button"
                    data-rc-filter-trigger="businessUnits"
                    className={`rc-thBtn ${
                      columnIsFiltered("businessUnits") ? "rc-thBtn--filtered" : ""
                    } ${activeFilter?.key === "businessUnits" ? "rc-thBtn--open" : ""}`}
                    onClick={(e) => toggleFilterPopover("businessUnits", e.currentTarget)}
                  >
                    <span className="rc-thLabel">Business unit</span>
                    <span className="rc-thSub">{columnSubtitle("businessUnits")}</span>
                    <span
                      className={`rc-thDot ${columnIsFiltered("businessUnits") ? "rc-thDot--on" : ""}`}
                      aria-hidden="true"
                    />
                  </button>
                </th>
                <th style={{ width: "18%" }}>
                  <button
                    type="button"
                    data-rc-filter-trigger="names"
                    className={`rc-thBtn ${columnIsFiltered("names") ? "rc-thBtn--filtered" : ""} ${
                      activeFilter?.key === "names" ? "rc-thBtn--open" : ""
                    }`}
                    onClick={(e) => toggleFilterPopover("names", e.currentTarget)}
                  >
                    <span className="rc-thLabel">Name</span>
                    <span className="rc-thSub">{columnSubtitle("names")}</span>
                    <span
                      className={`rc-thDot ${columnIsFiltered("names") ? "rc-thDot--on" : ""}`}
                      aria-hidden="true"
                    />
                  </button>
                </th>
                <th style={{ width: "24%" }}>
                  <button
                    type="button"
                    data-rc-filter-trigger="description"
                    className={`rc-thBtn ${
                      columnIsFiltered("description") ? "rc-thBtn--filtered" : ""
                    } ${activeFilter?.key === "description" ? "rc-thBtn--open" : ""}`}
                    onClick={(e) => toggleFilterPopover("description", e.currentTarget)}
                  >
                    <span className="rc-thLabel">Description</span>
                    <span className="rc-thSub">{columnSubtitle("description")}</span>
                    <span
                      className={`rc-thDot ${columnIsFiltered("description") ? "rc-thDot--on" : ""}`}
                      aria-hidden="true"
                    />
                  </button>
                </th>
                <th style={{ width: "10%" }}>
                  <button
                    type="button"
                    data-rc-filter-trigger="maxAdults"
                    className={`rc-thBtn rc-thBtn--right ${
                      columnIsFiltered("maxAdults") ? "rc-thBtn--filtered" : ""
                    } ${activeFilter?.key === "maxAdults" ? "rc-thBtn--open" : ""}`}
                    onClick={(e) => toggleFilterPopover("maxAdults", e.currentTarget)}
                  >
                    <span className="rc-thLabel">Max adults</span>
                    <span className="rc-thSub">{columnSubtitle("maxAdults")}</span>
                    <span
                      className={`rc-thDot ${columnIsFiltered("maxAdults") ? "rc-thDot--on" : ""}`}
                      aria-hidden="true"
                    />
                  </button>
                </th>
                <th style={{ width: "10%" }}>
                  <button
                    type="button"
                    data-rc-filter-trigger="maxChildren"
                    className={`rc-thBtn rc-thBtn--right ${
                      columnIsFiltered("maxChildren") ? "rc-thBtn--filtered" : ""
                    } ${activeFilter?.key === "maxChildren" ? "rc-thBtn--open" : ""}`}
                    onClick={(e) => toggleFilterPopover("maxChildren", e.currentTarget)}
                  >
                    <span className="rc-thLabel">Max children</span>
                    <span className="rc-thSub">{columnSubtitle("maxChildren")}</span>
                    <span
                      className={`rc-thDot ${columnIsFiltered("maxChildren") ? "rc-thDot--on" : ""}`}
                      aria-hidden="true"
                    />
                  </button>
                </th>
                <th style={{ width: "10%" }}>
                  <button
                    type="button"
                    data-rc-filter-trigger="maxOccupancy"
                    className={`rc-thBtn rc-thBtn--right ${
                      columnIsFiltered("maxOccupancy") ? "rc-thBtn--filtered" : ""
                    } ${activeFilter?.key === "maxOccupancy" ? "rc-thBtn--open" : ""}`}
                    onClick={(e) => toggleFilterPopover("maxOccupancy", e.currentTarget)}
                  >
                    <span className="rc-thLabel">Max occupancy</span>
                    <span className="rc-thSub">{columnSubtitle("maxOccupancy")}</span>
                    <span
                      className={`rc-thDot ${columnIsFiltered("maxOccupancy") ? "rc-thDot--on" : ""}`}
                      aria-hidden="true"
                    />
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredCategories.map((cat) => {
                const bu = businessUnitById.get(String(cat.business_unit_id));
                const maxOccupancy =
                  cat.max_occupancy ??
                  cat.maxOccupancy ??
                  (Number(cat.max_adults || 0) + Number(cat.max_children || 0));

                return (
                  <tr key={cat.id}>
                    <td>
                      <span className="rc-idPill">#{cat.id}</span>
                    </td>
                    <td>
                      <div className="rc-buCell">
                        <div className="rc-buName">
                          {bu?.name ?? `Business Unit ${cat.business_unit_id}`}
                        </div>
                        {bu?.name ? (
                          <div className="rc-buMeta">ID {cat.business_unit_id}</div>
                        ) : null}
                      </div>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="rc-nameBtn"
                        title="Edit"
                        onClick={() => openEditPopup(cat)}
                      >
                        {cat.name}
                      </button>
                    </td>
                    <td>
                      <div className="rc-desc" title={cat.description || ""}>
                        {cat.description || <span className="rc-muted">—</span>}
                      </div>
                    </td>
                    <td className="rc-numCell">
                      <span className="rc-pill">{cat.max_adults}</span>
                    </td>
                    <td className="rc-numCell">
                      <span className="rc-pill">{cat.max_children}</span>
                    </td>
                    <td className="rc-numCell">
                      <span className="rc-pill rc-pill--accent">{maxOccupancy}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {activeFilter?.key && filterModel && filterPopoverPos ? (
        <div
          ref={filterPopoverRef}
          className="rc-filterPopover"
          style={{ top: filterPopoverPos.top, left: filterPopoverPos.left, width: filterPopoverPos.width }}
        >
          <div className="rc-filterHeader">
            <div className="rc-filterTitle">{filterModel.title}</div>
            <div className="rc-filterHeaderActions">
              {columnIsFiltered(filterModel.key) ? (
                <button
                  type="button"
                  className="rc-filterSmallBtn"
                  onClick={() => clearColumnFilter(filterModel.key)}
                >
                  Clear
                </button>
              ) : null}

              {filterModel.type === "set" ? (
                <button type="button" className="rc-filterSmallBtn" onClick={selectAllVisibleOptions}>
                  All
                </button>
              ) : null}

              <button type="button" className="rc-filterCloseBtn" onClick={closeFilterPopover}>
                ×
              </button>
            </div>
          </div>

          <div className="rc-filterBody">
            {filterModel.type === "text" ? (
              <>
                <input
                  className="rc-filterInput"
                  value={columnFilters.description}
                  onChange={(e) =>
                    setColumnFilters((prev) => ({ ...prev, description: e.target.value }))
                  }
                  placeholder="Contiene…"
                  autoFocus
                />
                <div className="rc-filterHint">Filtra por texto dentro de la descripción.</div>
              </>
            ) : (
              <>
                <input
                  className="rc-filterInput"
                  value={filterOptionQuery}
                  onChange={(e) => setFilterOptionQuery(e.target.value)}
                  placeholder={`Buscar en ${filterModel.title.toLowerCase()}…`}
                  autoFocus
                />

                <div className="rc-filterList">
                  {visibleFilterOptions.length === 0 ? (
                    <div className="rc-filterHint">No hay opciones que coincidan.</div>
                  ) : (
                    visibleFilterOptions.map((opt) => {
                      const selected = Array.isArray(columnFilters[filterModel.key])
                        ? columnFilters[filterModel.key].includes(opt.value)
                        : false;

                      return (
                        <label key={String(opt.value)} className="rc-filterOption" title={opt.label}>
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleMultiSelectFilter(filterModel.key, opt.value)}
                          />
                          <span className="rc-filterOptionText">
                            <div className="rc-filterOptionLabel">{opt.label}</div>
                            {opt.meta ? <div className="rc-filterOptionMeta">{opt.meta}</div> : null}
                          </span>
                          <span className="rc-filterCountPill">{opt.count}</span>
                        </label>
                      );
                    })
                  )}
                </div>

                <div className="rc-filterHint">Selecciona uno o varios valores.</div>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>

      {/* --- POPUP NUEVO --- */}
      {showNewPopup && (
        <div style={{
          position: "fixed", left: 0, top: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 1001,
          display: "flex", alignItems: "center", justifyContent: "center"
        }}>
          <div style={{
            background: "#191933", color: "#fff", borderRadius: 10, padding: 32, minWidth: 390, boxShadow: "0 2px 14px #0009",
            border: "2px solid #333", position: "relative"
          }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="mb-0">Add Room Category</h4>
              <button onClick={() => setShowNewPopup(false)}
                style={{
                  background: "none", border: "none", color: "#fff", fontSize: 26, fontWeight: 400, cursor: "pointer"
                }}>×</button>
            </div>
            {/* --- CAMPOS DEL FORMULARIO --- */}
            <form onSubmit={e => { e.preventDefault(); handleSave(); }}>
              <div className="mb-2">
                <label style={{ fontWeight: 500 }}>ID</label>
                <input value="(auto)" disabled className="form-control form-control-sm bg-dark text-light" />
              </div>
              <div className="mb-2">
                <label style={{ fontWeight: 500 }}>Business Unit</label>
                <select
                  value={newCategory.business_unit_id}
                  onChange={e => setNewCategory(u => ({ ...u, business_unit_id: e.target.value }))}
                  className="form-control form-control-sm"
                  required
                >
                  <option value="">-- Select --</option>
                  {businessUnits.map(bu => (
                    <option value={bu.id} key={bu.id}>
                      {bu.id} – {bu.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-2">
                <label style={{ fontWeight: 500 }}>Name</label>
                <input
                  className="form-control form-control-sm"
                  value={newCategory.name}
                  onChange={e => setNewCategory(u => ({ ...u, name: e.target.value }))}
                  required
                />
              </div>
              <div className="mb-2">
                <label style={{ fontWeight: 500 }}>Description</label>
                <textarea
                  className="form-control form-control-sm"
                  value={newCategory.description}
                  onChange={e => setNewCategory(u => ({ ...u, description: e.target.value }))}
                  rows={2}
                  maxLength={4000}
                />
              </div>
              <div className="mb-2">
                <label style={{ fontWeight: 500 }}>Max Adults</label>
                <input
                  type="number"
                  min={1}
                  className="form-control form-control-sm"
                  value={newCategory.max_adults}
                  onChange={e => setNewCategory(u => ({ ...u, max_adults: e.target.value }))}
                  required
                />
              </div>
              <div className="mb-2">
                <label style={{ fontWeight: 500 }}>Max Children</label>
                <input
                  type="number"
                  min={0}
                  className="form-control form-control-sm"
                  value={newCategory.max_children}
                  onChange={e => setNewCategory(u => ({ ...u, max_children: e.target.value }))}
                  required
                />
              </div>
              <div className="mb-2">
                <label style={{ fontWeight: 500 }}>Max Occupancy</label>
                <input
                  type="number"
                  min={1}
                  className="form-control form-control-sm"
                  value={newCategory.max_occupancy}
                  onChange={e => setNewCategory(u => ({ ...u, max_occupancy: e.target.value }))}
                  required
                />
              </div>
              {error && <div className="text-danger mb-2">{error}</div>}
              <div className="d-flex gap-2 mt-4 justify-content-end">
                <button type="button" className="btn btn-secondary" onClick={() => setShowNewPopup(false)} disabled={saving}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? "Creating..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditPopup && editCategory && (
      <div style={{
        position: "fixed", left: 0, top: 0, width: "100vw", height: "100vh",
        background: "rgba(0,0,0,0.5)", zIndex: 1001,
        display: "flex", alignItems: "center", justifyContent: "center"
      }}>
        <div style={{
          background: "#191933", color: "#fff", borderRadius: 10,
          padding: 32, minWidth: 390, boxShadow: "0 2px 14px #0009",
          border: "2px solid #333", position: "relative"
        }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">Edit Room Category</h4>
            <button
              onClick={() => setShowEditPopup(false)}
              style={{
                background: "none", border: "none",
                color: "#fff", fontSize: 26, cursor: "pointer"
              }}
            >
              ×
            </button>
          </div>

          <form onSubmit={async e => {
            e.preventDefault();
            setSaving(true);
            setError("");

            try {
              const token = localStorage.getItem("palantir_token");
              await axios.put(
                `${process.env.REACT_APP_API_URL}/api/room_categories/${editCategory.id}`,
                editCategory,
                { headers: { Authorization: `Bearer ${token}` } }
              );

              const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/room_categories`,
                { headers: { Authorization: `Bearer ${token}` } }
              );

              setCategories(res.data);
              setShowEditPopup(false);
            } catch (err) {
              setError(err.response?.data?.error || "Error updating category.");
            } finally {
              setSaving(false);
            }
          }}>

            {/* ID */}
            <div className="mb-2">
              <label>ID</label>
              <input
                disabled
                value={editCategory.id}
                className="form-control form-control-sm bg-dark text-light"
              />
            </div>

            {/* Business Unit (bloqueado) */}
            <div className="mb-2">
              <label>Business Unit</label>
              <input
                disabled
                value={editCategory.business_unit_id}
                className="form-control form-control-sm bg-dark text-light"
              />
            </div>

            {/* Name */}
            <div className="mb-2">
              <label>Name</label>
              <input
                className="form-control form-control-sm"
                value={editCategory.name}
                onChange={e => setEditCategory(c => ({ ...c, name: e.target.value }))}
                required
              />
            </div>

            {/* Description */}
            <div className="mb-2">
              <label>Description</label>
              <textarea
                className="form-control form-control-sm"
                value={editCategory.description || ""}
                onChange={e => setEditCategory(c => ({ ...c, description: e.target.value }))}
                rows={2}
              />
            </div>

            {/* Max Adults */}
            <div className="mb-2">
              <label>Max Adults</label>
              <input
                type="number"
                min={1}
                className="form-control form-control-sm"
                value={editCategory.max_adults}
                onChange={e => setEditCategory(c => ({ ...c, max_adults: e.target.value }))}
                required
              />
            </div>

            {/* Max Children */}
            <div className="mb-2">
              <label>Max Children</label>
              <input
                type="number"
                min={0}
                className="form-control form-control-sm"
                value={editCategory.max_children}
                onChange={e => setEditCategory(c => ({ ...c, max_children: e.target.value }))}
                required
              />
            </div>

            {/* Max Occupancy */}
            <div className="mb-2">
              <label>Max Occupancy</label>
              <input
                type="number"
                min={1}
                className="form-control form-control-sm"
                value={editCategory.max_occupancy}
                onChange={e => setEditCategory(c => ({ ...c, max_occupancy: e.target.value }))}
                required
              />
            </div>

            {error && <div className="text-danger mb-2">{error}</div>}

            <div className="d-flex gap-2 mt-4 justify-content-end">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowEditPopup(false)}
                disabled={saving}
              >
                Cancel
              </button>

              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>

          </form>
        </div>
      </div>
    )}


      {showDeletePopup && (
        <div style={{
            position: "fixed", left: 0, top: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 1001,
            display: "flex", alignItems: "center", justifyContent: "center"
        }}>
            <div style={{
            background: "#191933", color: "#fff", borderRadius: 10, padding: 32, minWidth: 390, boxShadow: "0 2px 14px #0009",
            border: "2px solid #333", position: "relative"
            }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">Delete Room Category</h4>
                <button onClick={() => setShowDeletePopup(false)}
                style={{
                    background: "none", border: "none", color: "#fff", fontSize: 26, fontWeight: 400, cursor: "pointer"
                }}>×</button>
            </div>
            <form onSubmit={async e => {
                e.preventDefault();
                if (!deleteId) return;
                setDeleting(true);
                setDeleteError("");
                try {
                const token = localStorage.getItem("palantir_token");
                await axios.delete(`${process.env.REACT_APP_API_URL}/api/room_categories/${deleteId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setShowDeletePopup(false);
                // Refresca lista
                const updated = await axios.get(`${process.env.REACT_APP_API_URL}/api/room_categories`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setCategories(updated.data);
                } catch (err) {
                setDeleteError("No se pudo borrar la Room Category.");
                } finally {
                setDeleting(false);
                }
            }}>
                <div className="mb-2">
                <label style={{ fontWeight: 500 }}>Select Room Category to Delete</label>
                <select
                    className="form-control form-control-sm"
                    value={deleteId}
                    onChange={e => setDeleteId(e.target.value)}
                    required
                >
                    <option value="">-- Select --</option>
                    {categories.map(cat => (
                    <option value={cat.id} key={cat.id}>
                        {cat.id} – {cat.name}
                    </option>
                    ))}
                </select>
                </div>
                {deleteError && <div className="text-danger mb-2">{deleteError}</div>}
                <div className="d-flex gap-2 mt-4 justify-content-end">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeletePopup(false)} disabled={deleting}>Cancel</button>
                <button type="submit" className="btn btn-danger" disabled={deleting || !deleteId}>
                    {deleting ? "Deleting..." : "Delete"}
                </button>
                </div>
            </form>
            </div>
        </div>
        )}


    </div>
  );
}

export default RoomCategoriesList;
